import { Box, Stack, Typography } from "@mui/material";
import Tabs from './tabs';
import { useSelector } from "react-redux";
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CampaignIcon from '@mui/icons-material/Campaign';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';

import CampaignTitle from "./campaignTitle";
import IncludeExcludeTags from "./inclideExcludeTagUsers";
import CampaignCreation from "./campaignCreation";
import ScheduleCampaign from "./scheduleCampaign";

const BLUE = "#2563eb";
const GREEN = "#198754";

const STEPS = ["Campaign Details", "Audience", "Message", "Schedule"];

// ------------------- Top step bar: numbered pills joined by lines -------------------
function StepBar({ activeStep }) {
    return (
        <Stack
            direction="row"
            alignItems="center"
            sx={{
                p: { xs: 1, sm: 1.25 },
                borderRadius: 4,
                bgcolor: "#f8fafc",
                border: "1px solid #e9edf2",
                overflowX: "auto",
                "&::-webkit-scrollbar": { display: "none" },
            }}
        >
            {STEPS.map((label, i) => {
                const done = i < activeStep;
                const current = i === activeStep;
                return (
                    <Stack key={label} direction="row" alignItems="center" sx={{ flex: i < STEPS.length - 1 ? 1 : "0 0 auto", minWidth: 0 }}>
                        {/* pill */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{
                                flexShrink: 0,
                                pl: 0.5,
                                pr: { xs: current ? 1.5 : 0.5, sm: 1.75 },
                                py: 0.5,
                                borderRadius: 5,
                                transition: "all .2s ease",
                                bgcolor: current ? BLUE : done ? `${GREEN}14` : "transparent",
                                boxShadow: current ? "0 6px 14px -6px rgba(37,99,235,0.7)" : "none",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 13,
                                    fontWeight: 800,
                                    color: current ? BLUE : "#fff",
                                    bgcolor: current ? "#fff" : done ? GREEN : "#cbd5e1",
                                }}
                            >
                                {done ? <CheckRoundedIcon sx={{ fontSize: 17 }} /> : i + 1}
                            </Box>
                            <Typography
                                variant="body2"
                                noWrap
                                sx={{
                                    // phones: only the current step shows its name
                                    display: { xs: current ? "block" : "none", sm: "block" },
                                    fontWeight: current ? 800 : 700,
                                    color: current ? "#fff" : done ? GREEN : "#94a3b8",
                                }}
                            >
                                {label}
                            </Typography>
                        </Stack>
                        {/* connector */}
                        {i < STEPS.length - 1 && (
                            <Box sx={{ flex: 1, minWidth: 16, height: 2, mx: 1, borderRadius: 1, bgcolor: done ? GREEN : "#e2e8f0" }} />
                        )}
                    </Stack>
                );
            })}
        </Stack>
    );
}

// ------------------- Summary cards (display only) -------------------
function SummaryChip({ icon, label, value, color, hint }) {
    const filled = Boolean(value);
    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            title={hint || value || ""}
            sx={{
                position: "relative",
                minWidth: 0,
                px: 1.5,
                py: 1.1,
                borderRadius: 3,
                bgcolor: "#fff",
                border: `1px ${filled ? "solid" : "dashed"} ${filled ? "#e5e7eb" : "#cbd5e1"}`,
                borderLeft: `3px solid ${filled ? color : "#e2e8f0"}`,
                boxShadow: filled ? "0 1px 3px rgba(16,24,40,0.06)" : "none",
                transition: "all .2s ease",
            }}
        >
            <Box
                sx={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: filled ? color : "#cbd5e1",
                    bgcolor: filled ? `${color}14` : "#f8fafc",
                    "& svg": { fontSize: 19 },
                }}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" sx={{ display: "block", lineHeight: 1.2, color: "#94a3b8", fontWeight: 700, fontSize: 10.5, letterSpacing: 0.6, textTransform: "uppercase" }}>
                    {label}
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontSize: 14, fontWeight: filled ? 700 : 500, color: filled ? "#0f172a" : "#cbd5e1", lineHeight: 1.4 }}>
                    {value || "Not set yet"}
                </Typography>
            </Box>
            {/* done tick */}
            {filled && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        bgcolor: GREEN,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <CheckRoundedIcon sx={{ fontSize: 12 }} />
                </Box>
            )}
        </Stack>
    );
}

function SummaryStrip() {
    const { campaign_Title, campaignTagsDetails, tagWiseUsers, campaignFlowDetails, scheduleDate } = useSelector((state) => state.campaigns);
    const { getTemplates } = useSelector((state) => state.templates || {});

    // Audience: short value ("2 tags · 15 contacts"), full tag names on hover
    const include = campaignTagsDetails?.[0]?.includeTags || [];
    const tagNames = Array.isArray(include) ? include.map((t) => t?.label).filter(Boolean) : [];
    const audience = tagNames.length
        ? `${tagNames.length} tag${tagNames.length > 1 ? "s" : ""}${tagWiseUsers?.length ? ` · ${tagWiseUsers.length} contacts` : ""}`
        : "";

    const templateId = campaignFlowDetails?.[0]?.selectedTemplate;
    const templateName = templateId ? (getTemplates || []).find((t) => t.id === templateId)?.template_name || "" : "";

    const sched = scheduleDate?.[0];
    const schedule = sched?.schedulingTypeOption === 2 && sched?.schedulingDate
        ? (typeof sched.schedulingDate?.format === "function" ? sched.schedulingDate.format("DD-MM-YYYY hh:mm A") : String(sched.schedulingDate))
        : sched?.schedulingTypeOption === 1
            ? "Immediately"
            : "";

    return (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 1.5 }}>
            <SummaryChip icon={<CampaignIcon />} label="Name" value={campaign_Title} color={BLUE} />
            <SummaryChip icon={<LocalOfferOutlinedIcon />} label="Audience" value={audience} color="#7c3aed" hint={tagNames.join(", ")} />
            <SummaryChip icon={<DescriptionOutlinedIcon />} label="Template" value={templateName} color="#d97706" />
            <SummaryChip icon={<EventOutlinedIcon />} label="Schedule" value={schedule} color={GREEN} />
        </Box>
    );
}

// ------------------- Main Component -------------------
export default function CampaignSteper() {
    const { activeStep } = useSelector((state) => state.campaigns);

    return (
        <div>
            <Tabs>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {/* Step bar + step count */}
                    <Box>
                        <Stack direction="row" alignItems="baseline" justifyContent="flex-end" sx={{ mb: 1 }}>
                            <Typography variant="caption" fontWeight={700} sx={{ color: BLUE }}>
                                Step {Math.min(activeStep + 1, STEPS.length)} of {STEPS.length}
                            </Typography>
                        </Stack>
                        <StepBar activeStep={activeStep} />
                    </Box>

                    {/* What's been set so far */}
                    <SummaryStrip />

                    {/* Current step, full width */}
                    <Box>
                        {
                            activeStep === 0 ? (
                                <CampaignTitle />
                            ) : activeStep === 1 ? (
                                <IncludeExcludeTags />
                            ) : activeStep === 2 ? (
                                <CampaignCreation />
                            ) : activeStep === 3 ? (
                                <ScheduleCampaign />
                            ) : null
                        }
                    </Box>
                </Stack>
            </Tabs>
        </div>
    );
}
